import type { BBox, TrackWarning } from '@/lib/db/types';

import { registrationFromCallsign } from './identity';
import { sha256 } from './sha256';
import type { NormalisedTrack, ParsedPoint, ParsedTrack, Provenance } from './types';
import { ParseError } from './types';

/**
 * Turning a parser's output into the thing the database stores.
 *
 * This is the only place that decides what a track's numbers are, which is why
 * every judgement below is stated rather than implied. The governing rule from
 * `PLAN.md` §2 is that the track is evidence, not truth: nothing here silently
 * repairs data, it drops what it cannot trust and says so in a warning the
 * pilot reads.
 */

/** A gap this long without a fix is ADS-B coverage loss worth flagging. */
const COVERAGE_GAP_MS = 60_000;

/** Above this, an altitude is a corrupt field rather than a light aircraft. */
const MAX_PLAUSIBLE_ALT_FT = 60_000;

/** The Dead Sea shore is about -1,400 ft; below this is a bad fix. */
const MIN_PLAUSIBLE_ALT_FT = -1_500;

/** Whole seconds, which is as fine as a coverage gap is worth stating. */
function seconds(ms: number): string {
  return String(Math.round(ms / 1000));
}

export interface NormaliseOptions {
  coverageGapMs?: number;
}

function isUsablePosition(point: ParsedPoint): boolean {
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
    return false;
  }
  if (point.lat < -90 || point.lat > 90 || point.lon < -180 || point.lon > 180) {
    return false;
  }
  // Null Island. A zeroed position field is a dropped fix, not a point in the
  // Gulf of Guinea, and one of them stretches a bounding box across the planet.
  return !(point.lat === 0 && point.lon === 0);
}

/**
 * Canonical text whose digest is the track's identity.
 *
 * Only time and geometry go in — never the filename, the source format or the
 * callsign. That is deliberate: Flightradar24's KML and CSV exports of *one*
 * flight carry the same fixes in different containers, so hashing the fixes
 * alone means importing both is correctly recognised as a duplicate instead of
 * producing two copies of the same flight.
 *
 * The rounding is part of the contract. Six decimal places of latitude is about
 * 11 cm, and whole feet and knots are finer than ADS-B reports, so two files
 * describing the same fix agree exactly even when one of them arrived at its
 * altitude through a metric round trip.
 */
function contentHashOf(points: readonly ParsedPoint[]): string {
  const round = (value: number, places: number): string =>
    Number.isFinite(value) ? value.toFixed(places) : '';
  const canonical = points
    .map((p) =>
      [
        Number.isFinite(p.tMs) ? Math.round(p.tMs / 1000).toString() : '',
        round(p.lat, 6),
        round(p.lon, 6),
        round(p.altFt, 0),
        round(p.gsKt, 0),
        round(p.trackDeg, 0),
      ].join('|'),
    )
    .join('\n');
  return sha256(canonical);
}

/**
 * Vertical speed, derived because no file format this build reads supplies it.
 *
 * A central difference rather than a forward one: ADS-B altitude is quantised
 * to 25 ft, so a forward difference between two adjacent fixes a second apart
 * alternates between zero and 1,500 fpm and is useless to colour a track by.
 */
function deriveVerticalSpeed(t: Float64Array, altFt: Float32Array): Float32Array {
  const vs = new Float32Array(altFt.length).fill(Number.NaN);
  for (let i = 0; i < altFt.length; i += 1) {
    const before = i > 0 ? i - 1 : i;
    const after = i < altFt.length - 1 ? i + 1 : i;
    if (before === after) {
      continue;
    }
    const dt = ((t[after] ?? Number.NaN) - (t[before] ?? Number.NaN)) / 60_000;
    const dAlt = (altFt[after] ?? Number.NaN) - (altFt[before] ?? Number.NaN);
    if (Number.isFinite(dt) && dt > 0 && Number.isFinite(dAlt)) {
      vs[i] = dAlt / dt;
    }
  }
  return vs;
}

/**
 * Validate, order, de-duplicate and measure a parsed track.
 *
 * @throws ParseError if nothing usable survives, because an empty track in the
 *         flight list is worse than a message saying the file had no fixes.
 */
export function normaliseTrack(
  parsed: ParsedTrack,
  provenance: Provenance,
  options: NormaliseOptions = {},
): NormalisedTrack {
  const coverageGapMs = options.coverageGapMs ?? COVERAGE_GAP_MS;
  const warnings: TrackWarning[] = [...parsed.warnings];

  const usable = parsed.points.filter(isUsablePosition);
  const droppedForPosition = parsed.points.length - usable.length;

  if (usable.length === 0) {
    throw new ParseError(
      parsed.points.length === 0
        ? 'No position fixes found in this file.'
        : `All ${String(parsed.points.length)} fixes in this file had an unusable position.`,
    );
  }

  const timed = usable.filter((point) => Number.isFinite(point.tMs));
  const hasTimes = timed.length > 0;

  if (hasTimes && timed.length < usable.length) {
    warnings.push({
      kind: 'no-timestamps',
      message: `${String(usable.length - timed.length)} of ${String(usable.length)} fixes had no timestamp and were dropped.`,
    });
  }

  let ordered = hasTimes ? timed : usable;

  // Out of order before sorting is a real signal: a receiver clock stepping, or
  // two overlapping sources merged. Sorting hides it, so say it first.
  let outOfOrder = 0;
  for (let i = 1; i < ordered.length; i += 1) {
    if ((ordered[i]?.tMs ?? 0) < (ordered[i - 1]?.tMs ?? 0)) {
      outOfOrder += 1;
    }
  }
  if (outOfOrder > 0) {
    warnings.push({
      kind: 'clock-jump',
      message: `${String(outOfOrder)} fixes arrived out of time order and have been re-sorted.`,
    });
  }

  if (hasTimes) {
    ordered = [...ordered].sort((a, b) => a.tMs - b.tMs);
  }

  // Two fixes at the same instant are a repeat, not a hover. Keep the first.
  const deduped: ParsedPoint[] = [];
  let droppedForDuplicateTime = 0;
  for (const point of ordered) {
    const previous = deduped[deduped.length - 1];
    if (hasTimes && previous?.tMs === point.tMs) {
      droppedForDuplicateTime += 1;
      continue;
    }
    deduped.push(point);
  }

  const totalDropped = droppedForPosition + droppedForDuplicateTime;
  if (totalDropped > 0) {
    const reasons: string[] = [];
    if (droppedForPosition > 0) {
      reasons.push(`${String(droppedForPosition)} with an out-of-range position`);
    }
    if (droppedForDuplicateTime > 0) {
      reasons.push(`${String(droppedForDuplicateTime)} repeated at an instant already recorded`);
    }
    warnings.push({
      kind: 'dropped-points',
      message: `Dropped ${String(totalDropped)} fixes: ${reasons.join(', ')}.`,
    });
  }

  const count = deduped.length;
  const t = new Float64Array(count);
  const lat = new Float64Array(count);
  const lon = new Float64Array(count);
  const altFt = new Float32Array(count);
  const gsKt = new Float32Array(count);
  const trackDeg = new Float32Array(count);

  let west = Number.POSITIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  let altitudeCount = 0;
  let speedCount = 0;
  let movingSpeedCount = 0;
  let implausibleAltFrom = -1;
  let implausibleAltTo = -1;

  for (let i = 0; i < count; i += 1) {
    const point = deduped[i];
    if (point === undefined) {
      continue;
    }
    t[i] = point.tMs;
    lat[i] = point.lat;
    lon[i] = point.lon;
    altFt[i] = point.altFt;
    gsKt[i] = point.gsKt;
    trackDeg[i] = point.trackDeg;

    west = Math.min(west, point.lon);
    east = Math.max(east, point.lon);
    south = Math.min(south, point.lat);
    north = Math.max(north, point.lat);

    if (Number.isFinite(point.altFt)) {
      altitudeCount += 1;
      if (point.altFt > MAX_PLAUSIBLE_ALT_FT || point.altFt < MIN_PLAUSIBLE_ALT_FT) {
        implausibleAltFrom = implausibleAltFrom === -1 ? i : implausibleAltFrom;
        implausibleAltTo = i;
      }
    }
    if (Number.isFinite(point.gsKt)) {
      speedCount += 1;
      if (point.gsKt > 0) {
        movingSpeedCount += 1;
      }
    }
  }

  if (altitudeCount === 0) {
    warnings.push({
      kind: 'missing-altitude',
      message: 'This file carries no altitude, so the altitude profile and colouring are empty.',
    });
  }
  if (speedCount === 0) {
    warnings.push({
      kind: 'missing-speed',
      message: 'This file carries no groundspeed, so speed colouring is unavailable.',
    });
  } else if (movingSpeedCount === 0) {
    warnings.push({
      kind: 'missing-speed',
      message:
        'Every groundspeed in this file is zero, which is a source problem rather than a flight that never moved.',
    });
  }
  if (implausibleAltFrom !== -1) {
    warnings.push({
      kind: 'implausible-altitude',
      message: `Altitudes outside ${String(MIN_PLAUSIBLE_ALT_FT)} to ${String(MAX_PLAUSIBLE_ALT_FT)} ft are present and have been kept as read, not corrected.`,
      fromIndex: implausibleAltFrom,
      toIndex: implausibleAltTo,
    });
  }

  if (hasTimes) {
    /*
     * One warning for all of them, not one per gap. A patchy ADS-B track can
     * lose coverage dozens of times, and a list of dozens of near-identical
     * lines buries every other warning beside it — the pilot needs to know
     * that coverage dropped and how badly, which is a count and a duration,
     * not an itemised log. A single gap still reads as a single gap, and keeps
     * the fix indices either side of it; only the summary drops them, because
     * an aggregate is not localised to one range.
     */
    let gapCount = 0;
    let longestGapMs = 0;
    let totalGapMs = 0;
    let firstGapAt = -1;
    for (let i = 1; i < count; i += 1) {
      const gap = (t[i] ?? 0) - (t[i - 1] ?? 0);
      if (gap > coverageGapMs) {
        gapCount += 1;
        totalGapMs += gap;
        longestGapMs = Math.max(longestGapMs, gap);
        firstGapAt = firstGapAt === -1 ? i : firstGapAt;
      }
    }
    if (gapCount === 1) {
      warnings.push({
        kind: 'coverage-gap',
        message: `No fixes for ${seconds(totalGapMs)} s. The track is drawn straight across the gap; it is not what the aircraft flew.`,
        fromIndex: firstGapAt - 1,
        toIndex: firstGapAt,
      });
    } else if (gapCount > 1) {
      warnings.push({
        kind: 'coverage-gap',
        message: `Coverage dropped out ${String(gapCount)} times, for ${seconds(totalGapMs)} s in total and ${seconds(longestGapMs)} s at worst. The track is drawn straight across each gap; it is not what the aircraft flew.`,
      });
    }
  } else {
    warnings.push({
      kind: 'no-timestamps',
      message:
        'This file carries positions but no times. Duration, air time and the playback scrubber are unavailable, and the times shown are when it was imported.',
    });
  }

  const importedAt = provenance.importedAt ?? new Date().toISOString();
  /*
   * A timeless track still has to sort somewhere, and `startTime` is an index
   * Dexie will not store an `undefined` key for — a track without one would
   * simply vanish from the flight list. So it takes the import instant and
   * carries a `no-timestamps` warning, which is what the UI reads to show
   * "time unknown" instead of a duration that would be a fabrication.
   */
  const startTime = hasTimes ? new Date(t[0] ?? 0).toISOString() : importedAt;
  const endTime = hasTimes ? new Date(t[count - 1] ?? 0).toISOString() : importedAt;

  const callsign = parsed.callsign?.trim();
  const registration = parsed.registration ?? registrationFromCallsign(callsign);

  const bbox: BBox = [west, south, east, north];

  return {
    track: {
      id: provenance.id ?? crypto.randomUUID(),
      source: parsed.source,
      sourceRef: provenance.sourceRef,
      contentHash: contentHashOf(deduped),
      importedAt,
      startTime,
      endTime,
      bbox,
      pointCount: count,
      ...(callsign !== undefined && callsign !== '' ? { callsign } : {}),
      ...(registration !== undefined && registration !== '' ? { registration } : {}),
      ...(parsed.icaoHex !== undefined ? { icaoHex: parsed.icaoHex } : {}),
      warnings,
    },
    points: {
      trackId: '',
      t,
      lat,
      lon,
      altFt,
      gsKt,
      trackDeg,
      ...(altitudeCount > 0 && hasTimes ? { vsFpm: deriveVerticalSpeed(t, altFt) } : {}),
      altitudeRef: parsed.altitudeRef,
    },
  };
}

export { COVERAGE_GAP_MS, MAX_PLAUSIBLE_ALT_FT, MIN_PLAUSIBLE_ALT_FT };
